import React, { useEffect, useState } from 'react';
import { Table, TableColumn } from '@consta/uikit/Table';
import { Text } from '@consta/uikit/Text';
import { Card } from '@consta/uikit/Card';
import { Button } from '@consta/uikit/Button';
import { useLazyStatisticsQuery } from '../../../services/userService';
import { Statistics } from '../../../services/types/TUserServices';

// Тип для строки таблицы
type UserStatisticsRow = {
  id: string;
  fullName: string;
  role: string;
  email: string;
  lastLogin: string;
};

const StatisticsContent: React.FC = () => {
  const [triggerGetStatistics, { data: statisticsData, isLoading, error }] = useLazyStatisticsQuery();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [users, setUsers] = useState<Statistics[]>([]);

  // Обрабатываем данные когда они приходят
  useEffect(() => {
    if (statisticsData) {
      //('Received data:', statisticsData); // Для отладки
      setHasLoaded(true);
      
      // Извлекаем массив пользователей из results
      //@ts-ignore
      if (statisticsData.results && Array.isArray(statisticsData.results)) {
        //@ts-ignore
        setUsers(statisticsData.results);
      } else {
        setUsers([]);
      }
    }
  }, [statisticsData]);

  // Загружаем данные при монтировании компонента
  useEffect(() => {
    triggerGetStatistics();
  }, [triggerGetStatistics]);

  // Функция для ручной перезагрузки данных
  const handleRefresh = () => {
    triggerGetStatistics();
  };

  // Форматирование даты последнего входа
  const formatLastLogin = (lastLogin: string | null): string => {
    if (!lastLogin) return 'Никогда';
    
    try {
      const date = new Date(lastLogin);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Неизвестно';
    }
  };

  // Форматирование имени пользователя в формате "Фамилия И.О."
  const formatFullName = (user: Statistics): string => {
    const lastName = user.last_name || '';
    const firstName = user.first_name ? user.first_name[0] + '.' : '';
    const middleName = user.middle_name ? user.middle_name[0] + '.' : '';
    
    const parts = [lastName, firstName, middleName].filter(Boolean);
    
    return parts.join(' ') || user.short_name || user.username || 'Не указано';
  };

  // Преобразование данных для таблицы
  const rows: UserStatisticsRow[] = users.map((user, index) => ({
    id: `${user.username}-${index}`,
    fullName: formatFullName(user),
    role: user.role || 'Не указана',
    email: user.username || 'Не указан',
    lastLogin: formatLastLogin(user.last_login),
  }));

  // Колонки таблицы - только требуемые
  //@ts-ignore
  const columns: TableColumn<UserStatisticsRow>[] = [
    {
      title: 'Имя пользователя',
      accessor: 'fullName',
      sortable: true,
    },
    {
      title: 'Роль',
      accessor: 'role',
      sortable: true,
    },
    {
      title: 'Электронная почта',
      accessor: 'email',
      sortable: true,
    },
    {
      title: 'Последний вход',
      accessor: 'lastLogin',
      sortable: true,
    },
  ];

  if (isLoading && !hasLoaded) {
    return (
      <div style={{ padding: '20px' }}>
        <Text size="2xl" weight="bold">Статистика пользователей</Text>
        <Card verticalSpace="l" horizontalSpace="l" style={{ marginTop: '20px' }}>
          <Text>Загрузка данных...</Text>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Text size="2xl" weight="bold">Статистика пользователей</Text>
        <Card verticalSpace="l" horizontalSpace="l" style={{ marginTop: '20px' }}>
          <Text view="alert">Ошибка загрузки данных статистики</Text>
          <Button 
            label="Попробовать снова" 
            onClick={handleRefresh}
            style={{ marginTop: '10px' }}
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Text size="2xl" weight="bold">
          Статистика пользователей
        </Text>
      </div>
      
      <Card verticalSpace="l" horizontalSpace="l" style={{width: '85vw'}}>
        <Table
          columns={columns}
          rows={rows}
          stickyHeader
          verticalAlign="top"
          emptyRowsPlaceholder={
            <Text>
              {hasLoaded ? 'Нет данных для отображения' : 'Нажмите "Обновить" для загрузки данных'}
            </Text>
          }
        />
      </Card>
    </div>
  );
};

export default StatisticsContent;